<!--
  View Event Modal (Job Details / Event Details)
  Primary modal for viewing and editing event/job details on the OEE dashboard.
  Opened by clicking an event in the timeline. Supports both VIEW and inline EDIT
  modes (toggled via the pencil button). This is where users primarily view and edit jobs.
-->
<div class="oee-modal">
    <!-- Modal Header -->
    <div class="modal-header">
        <div class="header-content">
            @if (!showWorkOrderForm) {
                <div class="header-icon"
                    [class.batch-icon]="event.type === 'batch'"
                    [class.downtime-icon]="event.type === 'downtime'"
                    [class.incident-icon]="event.type === 'incident'">
                    <i class="fa" [ngClass]="{
                        'fa-box': event.type === 'batch',
                        'fa-exclamation-triangle': event.type === 'downtime',
                        'fa-exclamation-circle': event.type === 'incident'
                    }"></i>
                </div>
            }
            @if (showWorkOrderForm) {
                <div class="header-icon">
                    <i class="fa fa-print"></i>
                </div>
            }
            <div class="header-text">
                @if (!showWorkOrderForm) {
                    <h2>
                        {{ getModalTitle() }}: <span class="equipment-name">{{ event.equipment_name }}</span>
                        @if (isArchived()) {
                            <span class="archived-badge">
                                <i class="fa fa-archive"></i> Archived
                            </span>
                        }
                    </h2>
                }
                @if (showWorkOrderForm) {
                    <h2>Work Order Preview</h2>
                }
                <p class="subtitle">View and manage event details. Use the edit button to make changes.</p>
            </div>
        </div>
        <div class="header-actions">
            <!-- Print Work Order (downtime/incident events only) -->
            @if (!readOnly && isDowntimeOrErrorEvent() && !isArchived() && !isEditMode && !showWorkOrderForm) {
                <button mat-icon-button
                    class="print-btn"
                    (click)="printWorkOrder()"
                    [disabled]="!equipmentDetailsLoaded"
                    matTooltip="Print Work Order">
                    @if (equipmentDetailsLoaded) {
                        <mat-icon>print</mat-icon>
                    }
                    @if (!equipmentDetailsLoaded) {
                        <mat-spinner diameter="20"></mat-spinner>
                    }
                </button>
            }
            @if (!isArchived() && canEdit() && !showWorkOrderForm) {
                <button mat-icon-button class="edit-btn" (click)="toggleEditMode()" [matTooltip]="isEditMode ? 'Cancel Edit' : 'Edit Event'">
                    <mat-icon>{{ isEditMode ? 'edit_off' : 'edit' }}</mat-icon>
                </button>
            }
            <button class="close-btn" mat-dialog-close (click)="showWorkOrderForm ? cancelWorkOrder() : closeModal()" [matTooltip]="showWorkOrderForm ? 'Back to Event' : 'Close'" cdkFocusInitial>
                <mat-icon>{{ showWorkOrderForm ? 'arrow_back' : 'close' }}</mat-icon>
            </button>
        </div>
    </div>

  <mat-dialog-content>
    <!-- ===== WORK ORDER FORM (shown when showWorkOrderForm is true) ===== -->
    @if (showWorkOrderForm) {
      <div class="work-order-form-container">
        <p class="work-order-hint">Edit any fields below, then click Print to send to your printer.</p>
        <div class="work-order-content">
          <div class="work-order-header">
            <h1>CORRECTIVE MAINTENANCE WORK ORDER</h1>
          </div>
          <table class="work-order-table">
            <tbody>
              <!-- Row 1: Equipment Number + Equipment Location -->
              <tr class="two-col-row">
                <td class="field-label">1. Equipment Number:</td>
                <td class="field-value"><input type="text" value="{{ getEquipmentCode() }}"></td>
                <td class="field-label">2. Equipment Location:</td>
                <td class="field-value"><input type="text" value="{{ getEquipmentLocation() }}"></td>
              </tr>
              <!-- Row 2: Equipment Name + Request Date & Time -->
              <tr class="two-col-row">
                <td class="field-label">3. Equipment Name:</td>
                <td class="field-value"><input type="text" value="{{ event.equipment_name || '' }}"></td>
                <td class="field-label">4. Request Date &amp; Time:</td>
                <td class="field-value"><input type="text" value="{{ getCurrentDateTime() }}"></td>
              </tr>
              <!-- Row 3: Requested By (Name + Emp #) -->
              <tr class="two-col-row">
                <td class="field-label">5. Requested By:</td>
                <td class="field-value"><input type="text" value="{{ event.created_by_name || '' }}"></td>
                <td class="field-label">Emp #:</td>
                <td class="field-value"><input type="text" value="{{ getRequestedByEmployeeNumber() }}"></td>
              </tr>
              <!-- Row 4: Technician Name (Name + Emp #) -->
              <tr class="two-col-row">
                <td class="field-label">6. Technician Name:</td>
                <td class="field-value"><input type="text" value="{{ event.troubleshooting_started_by_name || '' }}"></td>
                <td class="field-label">Emp #:</td>
                <td class="field-value"><input type="text" value="{{ getTechnicianEmployeeNumber() }}"></td>
              </tr>
              <!-- Row 5: Reason (full width) -->
              <tr>
                <td class="field-label">7. {{ getReasonLabel() }}:</td>
                <td class="field-value" colspan="3"><input type="text" value="{{ getReasonDisplay() }}"></td>
              </tr>
              <!-- Row 6: Description of Problem (full width textarea) -->
              <tr class="textarea-row">
                <td class="field-label">8. Description of Problem:</td>
                <td class="field-value" colspan="3"><textarea>{{ getProblemDescription() }}</textarea></td>
              </tr>
              <!-- Row 7: Description of Repair Work (full width textarea) -->
              <tr class="textarea-row">
                <td class="field-label">9. Description of Repair Work:</td>
                <td class="field-value" colspan="3"><textarea>{{ event.resolution_notes || '' }}</textarea></td>
              </tr>
              <!-- Row 8: Part # Used (full width) -->
              <tr>
                <td class="field-label">10. Part # Used:</td>
                <td class="field-value" colspan="3"><input type="text" value=""></td>
              </tr>
              <!-- Row 9: Equipment Down Time - Start + End -->
              <tr class="two-col-row">
                <td class="field-label">11. Downtime Start:</td>
                <td class="field-value"><input type="text" value="{{ getDowntimeStartDisplay() }}"></td>
                <td class="field-label">Downtime End:</td>
                <td class="field-value"><input type="text" value="{{ getDowntimeEndDisplay() }}"></td>
              </tr>
              <!-- Row 10: Actual Repair Time - Start + End -->
              <tr class="two-col-row">
                <td class="field-label">12. Repair Start:</td>
                <td class="field-value"><input type="text" value="{{ getRepairStartDisplay() }}"></td>
                <td class="field-label">Repair End:</td>
                <td class="field-value"><input type="text" value="{{ getRepairEndDisplay() }}"></td>
              </tr>
            </tbody>
          </table>
          <div class="signature-section">
            <div class="signature-row">
              <div class="signature-field">
                <span class="sig-label">13. Equipment Operator Signature:</span>
                <input type="text" class="signature-input">
              </div>
              <div class="signature-field date-field">
                <span class="sig-label">Date:</span>
                <input type="text" class="date-input">
              </div>
            </div>
            <div class="signature-row">
              <div class="signature-field">
                <span class="sig-label">14. Technician Signature:</span>
                <input type="text" class="signature-input">
              </div>
              <div class="signature-field date-field">
                <span class="sig-label">Date:</span>
                <input type="text" class="date-input">
              </div>
            </div>
          </div>
          <!-- CMWO Number at very bottom -->
          <div class="cmwo-section">
            <span class="cmwo-label">15. CMWO #:</span>
            <input type="text" class="cmwo-input" value="{{ formatCMWONumber() }}">
          </div>
          <div class="work-order-footer">
            Printed from Kartorium OEE Dashboard
          </div>
        </div>
      </div>
    }

    <!-- ===== EVENT VIEW (hidden when showWorkOrderForm is true) ===== -->
    @if (!showWorkOrderForm) {
      <div>
        <!-- Archived Banner -->
        @if (isArchived()) {
          <div class="archived-banner">
            <div class="archived-header">
              <i class="fa fa-archive"></i>
              <span class="archived-title">This event is archived</span>
            </div>
            <div class="archived-details">
              <div class="archived-meta">
                <span>Archived by <strong>{{ getArchivedBy() }}</strong></span>
                <span class="separator">•</span>
                <span>{{ formatTimestamp(getArchivedAt()) }}</span>
              </div>
              @if (event.archive_reason) {
                <div class="archived-reason">
                  <span class="reason-label">Reason:</span> {{ event.archive_reason }}
                </div>
              }
            </div>
          </div>
        }
        <!-- VIEW MODE -->
        @if (!isEditMode) {
          <div class="view-mode-content">
            <!-- Basic Info Card -->
            <div class="info-card">
              <div class="card-header">
                <i class="fa fa-info-circle"></i>
                <h3>Basic Information</h3>
              </div>
              <div class="card-content">
                <div class="info-row">
                  <span class="label">Event ID</span>
                  <span class="value">#{{ event.event_id }}</span>
                </div>
                <!-- Hide Type row for job events (redundant with dynamic title) -->
                @if (!isBatchTypeEvent()) {
                  <div class="info-row">
                    <span class="label">Type</span>
                    <span class="value">{{ event.type | titlecase }}</span>
                  </div>
                }
                <div class="info-row">
                  <span class="label">Equipment</span>
                  <span class="value">{{ event.equipment_name || 'N/A' }}</span>
                </div>
                @if (stationName) {
                  <div class="info-row">
                    <span class="label">Station</span>
                    <span class="value">{{ stationName }}</span>
                  </div>
                }
                <!-- Simplified status for job events (Running/Completed/Aborted) -->
                @if (isBatchTypeEvent() && getSimplifiedBatchStatus()) {
                  <div class="info-row">
                    <span class="label">Status</span>
                    <span class="value batch-status-badge"
                      [class.running]="getSimplifiedBatchStatus() === 'Active'"
                      [class.idle]="getSimplifiedBatchStatus() === 'Idle'"
                      [class.ready-for-review]="getSimplifiedBatchStatus() === 'Ready for Review'"
                      [class.approved]="getSimplifiedBatchStatus() === 'Approved'"
                      [class.completed]="getSimplifiedBatchStatus() === 'Completed'"
                      [class.aborted]="getSimplifiedBatchStatus() === 'Aborted'">
                      {{ getSimplifiedBatchStatus() }}
                    </span>
                  </div>
                }
                @if (isBatchTypeEvent() && getSimplifiedBatchStatus() === 'Aborted' && event.batch?.abort_reason) {
                  <div class="info-row abort-reason-row">
                    <span class="label">Abort Reason</span>
                    <span class="value abort-reason-value">
                      <span class="abort-code">{{ event.batch.abort_reason.code }}</span>
                      <span class="abort-name">{{ event.batch.abort_reason.name }}</span>
                    </span>
                  </div>
                }
                <!-- Original status for non-job events -->
                @if (!isBatchTypeEvent() && event.type !== 'incident') {
                  <div class="info-row">
                    <span class="label">Status</span>
                    <span class="value status-badge" [class.ongoing]="isOngoing()" [class.resolved]="!isOngoing()">
                      {{ isOngoing() ? 'Ongoing' : 'Resolved' }}
                    </span>
                  </div>
                }
              </div>
            </div>
            <!-- Linked Equipment Card (for multi-equipment events) -->
            @if (hasLinkedEquipment()) {
              <div class="info-card linked-equipment-card">
                <div class="card-header">
                  <i class="fa fa-link"></i>
                  <h3>Linked Equipment</h3>
                  <span class="linked-count-badge">
                    {{ event.linked_equipment_names?.length || 0 }} linked
                  </span>
                </div>
                <div class="card-content">
                  <p class="linked-description">
                    This event affects multiple equipment. The downtime is recorded across all linked equipment.
                  </p>
                  <div class="linked-equipment-list">
                    @for (name of event.linked_equipment_names; track name) {
                      <div class="linked-equipment-item">
                        <i class="fa fa-cube"></i>
                        <span>{{ name }}</span>
                      </div>
                    }
                  </div>
                </div>
              </div>
            }
            <!-- Timing Card -->
            <div class="info-card">
              <div class="card-header">
                <i class="fa fa-clock"></i>
                <h3>Timing</h3>
              </div>
              <div class="card-content">
                <div class="info-row">
                  <span class="label">Started</span>
                  <span class="value">{{ formatTimestamp(event.start_ts) }}</span>
                </div>
                @if (event.end_ts) {
                  <div class="info-row">
                    <span class="label">Ended</span>
                    <span class="value">{{ formatTimestamp(event.end_ts) }}</span>
                  </div>
                }
                <div class="info-row">
                  <span class="label">Duration</span>
                  <span class="value">{{ formatDuration(event.duration_minutes) }}</span>
                </div>
                <!-- Parts/hour rate for job events -->
                @if (isBatchTypeEvent() && calculatePartsPerHour()) {
                  <div class="info-row">
                    <span class="label">Parts/Hour</span>
                    <span class="value rate-value">
                      <i class="fa fa-tachometer-alt"></i>
                      {{ calculatePartsPerHour() }}
                    </span>
                  </div>
                }
              </div>
            </div>
            <!-- Job Information Card (for job-type events only - inline display) -->
            @if (isBatchTypeEvent() && event.batch) {
              <div class="info-card batch-info-card">
                <div class="card-header">
                  <i class="fa fa-box"></i>
                  <h3>Job Information</h3>
                </div>
                <div class="card-content">
                  @if (event.batch.batch_number) {
                    <div class="info-row">
                      <span class="label">Job Number</span>
                      <!--
                        Opens in a new tab so the View Event modal stays open
                        behind it — the operator can keep editing the event
                        while exploring the focus page beside it.
                      -->
                      <a class="value batch-number batch-focus-link"
                         [routerLink]="['/oee/job-history', event.batch.batch_number]"
                         target="_blank"
                         rel="noopener"
                         (click)="$event.stopPropagation()"
                         data-testid="view-event-modal-batch-link">
                        {{ event.batch.batch_number }}
                        <i class="fa fa-external-link-alt small ms-1" aria-hidden="true"></i>
                      </a>
                    </div>
                  }
                  @if (event.batch.work_order) {
                    <div class="info-row">
                      <span class="label">Work Order</span>
                      <span class="value">{{ event.batch.work_order }}</span>
                    </div>
                  }
                  @if (event.batch.product_name) {
                    <div class="info-row">
                      <span class="label">Product</span>
                      <span class="value">
                        @if (event.batch.product_id) {
                          <a [routerLink]="['/oee/products', event.batch.product_id]" target="_blank">{{ event.batch.product_name }}@if (event.batch.product_code) { ({{ event.batch.product_code }})}</a>
                        } @else {
                          {{ event.batch.product_name }}@if (event.batch.product_code) { ({{ event.batch.product_code }})}
                        }
                      </span>
                    </div>
                  }
                  <!-- Parts Summary Row -->
                  @if (event.batch.good_parts !== undefined || event.batch.reject_parts !== undefined) {
                    <div class="parts-summary-row">
                      <div class="parts-stat good">
                        <i class="fa fa-check-circle"></i>
                        <span class="count">{{ event.batch.good_parts || 0 }}</span>
                        <span class="stat-label">Good</span>
                      </div>
                      @if ((event.batch.reject_parts || 0) > 0) {
                        <div class="parts-stat reject">
                          <i class="fa fa-times-circle"></i>
                          <span class="count">{{ event.batch.reject_parts }}</span>
                          <span class="stat-label">Scrap</span>
                        </div>
                      }
                      <div class="parts-stat total">
                        <i class="fa fa-cubes"></i>
                        <span class="count">{{ (event.batch.good_parts || 0) + (event.batch.reject_parts || 0) }}</span>
                        <span class="stat-label">Total</span>
                      </div>
                    </div>
                  }
                  <!-- Assigned Operators -->
                  @if (batchOperators.length > 0) {
                    <div class="info-row">
                      <span class="label">Operators</span>
                      <span class="value">
                        @for (op of batchOperators; track op.user_id; let last = $last) {
                          <a class="operator-focus-link"
                             data-testid="operator-focus-link"
                             [routerLink]="['/oee/operators', op.user_id]"
                             target="_blank"
                             rel="noopener"
                             (click)="$event.stopPropagation()">{{ op.display_name || 'Unknown user' }}</a><span>{{ last ? '' : ', ' }}</span>
                        }
                      </span>
                    </div>
                  }
                  <!-- Equipment -->
                  @if (batchEquipment.length > 0) {
                    <div class="info-row">
                      <span class="label">Additional Equipment</span>
                      <span class="value">
                        @for (eq of batchEquipment; track eq.equipment_id; let last = $last) {
                          <span>{{ eq.equipment_name }} ({{ eq.equipment_code }}){{ last ? '' : ', ' }}</span>
                        }
                      </span>
                    </div>
                  }
                </div>
                <!-- Job Actions for running jobes -->
                @if (!readOnly && isBatchInProgress()) {
                  <div class="card-actions">
                    <button mat-stroked-button color="primary" (click)="openCompleteBatchModal()">
                      <mat-icon>check_circle</mat-icon>
                      {{ completeBatchActionLabel }}
                    </button>
                    <button mat-stroked-button color="warn" (click)="openAbortBatchModal()">
                      <mat-icon>cancel</mat-icon>
                      Abort Job
                    </button>
                  </div>
                }
                <!-- Job Actions for aborted jobs (Issue #2677) -->
                @if (!readOnly && isBatchAborted()) {
                  <div class="card-actions">
                    <button mat-stroked-button color="primary" (click)="reopenBatch()" [disabled]="reopening">
                      @if (reopening) {
                        <mat-spinner diameter="18" class="me-1"></mat-spinner>
                      }
                      @if (!reopening) {
                        <mat-icon>refresh</mat-icon>
                      }
                      {{ reopening ? 'Reopening...' : 'Reopen Job' }}
                    </button>
                  </div>
                }
              </div>
            }
            <!-- Custom Fields Card (for job-type events only) -->
            @if (isBatchTypeEvent() && (customFieldValues.length > 0 || loadingCustomFields)) {
              <div class="info-card custom-fields-card"
                >
                <div class="card-header">
                  <i class="fa fa-sliders-h"></i>
                  <h3>Custom Fields</h3>
                  @if (loadingCustomFields) {
                    <mat-spinner diameter="16"></mat-spinner>
                  }
                </div>
                @if (!loadingCustomFields) {
                  <div class="card-content">
                    @for (cfv of customFieldValues; track cfv) {
                      <div class="info-row">
                        <span class="label">{{ cfv.field_label }}</span>
                        <span class="value">{{ formatCustomFieldValue(cfv) }}</span>
                      </div>
                    }
                    @if (customFieldValues.length === 0 && customFields.length > 0) {
                      <div class="empty-state">
                        <span class="text-muted">No custom field values set</span>
                      </div>
                    }
                  </div>
                }
              </div>
            }
            @if (isBatchTypeEvent() && event.batch && (equipmentDetails?.allows_batch_operators !== false || equipmentDetails?.restrict_batch_equipment === true)) {
              <div class="info-card time-entries-card">
                <div class="card-header">
                  <i class="fa fa-user-clock"></i>
                  <h3>Time Entries</h3>
                </div>
                <div class="card-content p-0">
                  <app-batch-time-entries
                      [batchId]="event.batch.batch_id"
                      [equipmentId]="event.equipment_id"
                      [siteId]="selectedSiteId"
                      mode="full"
                      [readOnly]="true">
                  </app-batch-time-entries>
                </div>
              </div>
            }
            <!-- Associated Downtime/Incidents Card (for job events with linked events) -->
            @if (isBatchTypeEvent() && linkedDowntimeIncidentEvents.length > 0) {
              <div class="info-card linked-events-card">
                <div class="card-header">
                  <i class="fa fa-exclamation-triangle"></i>
                  <h3>Associated Events</h3>
                  <span class="linked-count-badge">
                    {{ linkedDowntimeIncidentEvents.length }} event{{ linkedDowntimeIncidentEvents.length !== 1 ? 's' : '' }}
                  </span>
                </div>
                <div class="card-content">
                  <div class="linked-events-list">
                    @for (evt of linkedDowntimeIncidentEvents; track evt.event_id) {
                      <div class="linked-item-row" (click)="openLinkedEvent(evt)">
                        <div class="linked-item-main">
                          <span class="linked-item-name">{{ evt.downtime_reason || evt.incident_reason || 'No reason' }}</span>
                          <span class="event-type-badge"
                            [class.downtime]="evt.event_type === 'downtime'"
                            [class.incident]="evt.event_type === 'incident'">
                            {{ evt.event_type | titlecase }}
                          </span>
                          @if (evt.auto_linked) {
                            <span class="auto-tag" title="Automatically associated by time overlap">auto</span>
                          }
                        </div>
                        <div class="linked-item-details">
                          <span>{{ evt.equipment_name }}</span>
                          <span class="detail-separator">&middot;</span>
                          <span>{{ formatTimestamp(evt.start_ts) }} &mdash; {{ evt.end_ts ? formatTimestamp(evt.end_ts) : 'Ongoing' }}</span>
                          @if (evt.duration_minutes) {
                            <span class="detail-separator">&middot;</span>
                            <span>{{ formatDuration(evt.duration_minutes) }}</span>
                          }
                        </div>
                      </div>
                    }
                  </div>
                </div>
              </div>
            }
            @if (isBatchTypeEvent() && loadingLinkedEvents) {
              <div class="info-card">
                <div class="card-header">
                  <i class="fa fa-exclamation-triangle"></i>
                  <h3>Associated Events</h3>
                  <mat-spinner diameter="20"></mat-spinner>
                </div>
              </div>
            }
            <!-- Downtime Details Card (only for downtime events) -->
            @if (event.type === 'downtime') {
              <div class="info-card">
                <div class="card-header">
                  <i class="fa fa-exclamation-triangle"></i>
                  <h3>Downtime Details</h3>
                </div>
                <div class="card-content">
                  @if (event.downtime_reason) {
                    <div class="info-row">
                      <span class="label">Reason Code</span>
                      <span class="value reason-code">
                        <span class="code">{{ event.downtime_reason.code }}</span>
                        {{ event.downtime_reason.display_name }}
                      </span>
                    </div>
                  }
                  @if (getGroupName()) {
                    <div class="info-row">
                      <span class="label">Group</span>
                      <span class="value">{{ getGroupName() }}</span>
                    </div>
                  }
                  <div class="info-row">
                    <span class="label">Downtime Type</span>
                    <span class="value downtime-type" [class.planned]="getDowntimeReasonType() === 'planned'"
                      [class.unplanned]="getDowntimeReasonType() === 'unplanned'"
                      [class.idle]="getDowntimeReasonType() === 'idle'">
                      {{ getDowntimeReasonType() | titlecase }}
                    </span>
                  </div>
                </div>
              </div>
            }
            <!-- Incident Details Card (only for incident events) -->
            @if (event.type === 'incident') {
              <div class="info-card">
                <div class="card-header">
                  <i class="fa fa-exclamation-circle"></i>
                  <h3>Incident Details</h3>
                </div>
                <div class="card-content">
                  @if (event.incident_reason) {
                    <div class="info-row">
                      <span class="label">Reason Code</span>
                      <span class="value reason-code">
                        <span class="code">{{ event.incident_reason.code }}</span>
                        {{ event.incident_reason.name }}
                      </span>
                    </div>
                  }
                  @if (!event.incident_reason && event.incident_reason_id) {
                    <div class="info-row">
                      <span class="label">Reason Code</span>
                      <span class="value text-muted">Loading...</span>
                    </div>
                  }
                  @if (!event.incident_reason && !event.incident_reason_id) {
                    <div class="info-row">
                      <span class="label">Reason Code</span>
                      <span class="value text-muted">No reason assigned</span>
                    </div>
                  }
                  @if (getIncidentGroupName()) {
                    <div class="info-row">
                      <span class="label">Category</span>
                      <span class="value">{{ getIncidentGroupName() }}</span>
                    </div>
                  }
                  @if (event.incident_reason?.description) {
                    <div class="info-row">
                      <span class="label">Description</span>
                      <span class="value">{{ event.incident_reason.description }}</span>
                    </div>
                  }
                </div>
              </div>
            }
            <!-- Associated Jobs Card (for downtime/incident events with linked jobs) -->
            @if ((event.type === 'downtime' || event.type === 'incident') && linkedBatchEvents.length > 0) {
              <div class="info-card linked-batches-card">
                <div class="card-header">
                  <i class="fa fa-boxes"></i>
                  <h3>Associated Jobs</h3>
                  <span class="linked-count-badge">
                    {{ linkedBatchEvents.length }} batch{{ linkedBatchEvents.length !== 1 ? 'es' : '' }}
                  </span>
                </div>
                <div class="card-content">
                  <div class="linked-batches-list">
                    @for (batch of linkedBatchEvents; track batch.batch_event_id) {
                      <div class="linked-item-row" (click)="openLinkedBatch(batch)">
                        <div class="linked-item-main">
                          <a class="linked-item-name batch-focus-link"
                             [routerLink]="['/oee/job-history', batch.batch_number]"
                             target="_blank"
                             rel="noopener"
                             (click)="$event.stopPropagation()">{{ batch.batch_number }}</a>
                          <span class="batch-status-badge"
                            [class.running]="batch.status === 'running' || batch.status === 'in_progress'"
                            [class.idle]="batch.status === 'idle'"
                            [class.ready-for-review]="batch.status === 'ready_for_review'"
                            [class.approved]="batch.status === 'approved'"
                            [class.completed]="batch.status === 'completed'"
                            [class.aborted]="batch.status === 'aborted'">
                            {{ getLinkedBatchLabel(batch) }}
                          </span>
                          @if (batch.auto_linked) {
                            <span class="auto-tag" title="Automatically associated by time overlap">auto</span>
                          }
                        </div>
                        <div class="linked-item-details">
                          <span>{{ batch.equipment_name }}</span>
                          <span class="detail-separator">·</span>
                          <span>{{ formatTimestamp(batch.start_ts) }} — {{ batch.end_ts ? formatTimestamp(batch.end_ts) : 'Ongoing' }}</span>
                          @if (batch.work_order) {
                            <span class="detail-separator">·</span>
                            <span>WO: {{ batch.work_order }}</span>
                          }
                        </div>
                      </div>
                    }
                  </div>
                </div>
              </div>
            }
            @if ((event.type === 'downtime' || event.type === 'incident') && loadingLinkedBatches) {
              <div class="info-card">
                <div class="card-header">
                  <i class="fa fa-boxes"></i>
                  <h3>Associated Jobs</h3>
                  <mat-spinner diameter="20"></mat-spinner>
                </div>
              </div>
            }
            <!-- Troubleshooting Status Card (only for downtime events) -->
            @if (event.type === 'downtime' && getTroubleshootingStatus() !== 'Not Started') {
              <div class="info-card">
                <div class="card-header">
                  <i class="fa fa-wrench"></i>
                  <h3>Troubleshooting Status</h3>
                  <span class="status-badge" [ngClass]="getTroubleshootingStatusClass()">
                    {{ getTroubleshootingStatus() || 'Not Started' }}
                  </span>
                </div>
                @if (getTroubleshootingStatus() !== 'Not Started') {
                  <div class="card-content">
                    @if (event.troubleshooting_started_at) {
                      <div class="info-row">
                        <span class="label">Troubleshooting Started</span>
                        <span class="value">{{ formatTimestamp(event.troubleshooting_started_at) }}</span>
                      </div>
                    }
                    @if (event.troubleshooting_started_by || event.troubleshooting_started_by_name) {
                      <div class="info-row"
                        >
                        <span class="label">Troubleshooting Started By</span>
                        <span class="value">{{ getUserDisplayName(event.troubleshooting_started_by, event.troubleshooting_started_by_name) }}</span>
                      </div>
                    }
                    @if (getTimeToAddressMinutes() !== null) {
                      <div class="info-row">
                        <span class="label">Time to Address</span>
                        <span class="value">{{ formatDuration(getTimeToAddressMinutes()) }}</span>
                      </div>
                    }
                    @if (event.resolved_at) {
                      <div class="info-row">
                        <span class="label">Resolved At</span>
                        <span class="value">{{ formatTimestamp(event.resolved_at) }}</span>
                      </div>
                    }
                    @if (event.resolved_by || event.resolved_by_name) {
                      <div class="info-row">
                        <span class="label">Resolved By</span>
                        <span class="value">{{ getUserDisplayName(event.resolved_by, event.resolved_by_name) }}</span>
                      </div>
                    }
                    @if (getTimeToFixMinutes() !== null) {
                      <div class="info-row">
                        <span class="label">Time to Fix</span>
                        <span class="value">{{ formatDuration(getTimeToFixMinutes()) }}</span>
                      </div>
                    }
                  </div>
                }
              </div>
            }
            <!-- Jobs Section (for downtime events with overlapping jobs) -->
            @if (shouldShowBatchesSection()) {
              <div class="info-card batches-card"
                [class.collapsed]="!batchesSectionExpanded">
                <div class="card-header clickable" (click)="toggleBatchesSection()">
                  <i class="fa fa-boxes"></i>
                  <h3>Jobs</h3>
                  @if (!loadingBatches) {
                    <span class="batch-count-badge">
                      {{ event.batches?.length || 0 }} batch{{ (event.batches?.length || 0) !== 1 ? 'es' : '' }}
                    </span>
                  }
                  @if (loadingBatches) {
                    <mat-spinner diameter="20"></mat-spinner>
                  }
                  @if (!loadingBatches) {
                    <button mat-icon-button class="expand-btn">
                      <mat-icon>{{ batchesSectionExpanded ? 'expand_less' : 'expand_more' }}</mat-icon>
                    </button>
                  }
                </div>
                @if (batchesSectionExpanded) {
                  <div class="batches-content">
                    <!-- Loading state -->
                    @if (loadingBatches) {
                      <div class="card-content">
                        <p class="loading-text">Loading batches...</p>
                      </div>
                    }
                    <!-- No jobs -->
                    @if (!loadingBatches && (!event.batches || event.batches.length === 0)) {
                      <div class="card-content">
                        <p class="no-batches-text">No jobs associated with this event.</p>
                      </div>
                    }
                    <!-- Batches summary -->
                    @if (!loadingBatches && event.batches && event.batches.length > 0) {
                      <div class="batches-summary">
                        <div class="summary-stats">
                          <div class="stat">
                            <span class="stat-value running">{{ getRunningBatchesCount() }}</span>
                            <span class="stat-label">Active</span>
                          </div>
                          <div class="stat">
                            <span class="stat-value completed">{{ getCompletedBatchesCount() }}</span>
                            <span class="stat-label">Idle</span>
                          </div>
                          @if (getTotalPartsAcrossBatches() > 0) {
                            <div class="stat">
                              <span class="stat-value">{{ getTotalPartsAcrossBatches() }}</span>
                              <span class="stat-label">Total Parts</span>
                            </div>
                          }
                          @if (getTotalGoodPartsAcrossBatches() > 0) {
                            <div class="stat">
                              <span class="stat-value good">{{ getTotalGoodPartsAcrossBatches() }}</span>
                              <span class="stat-label">Good</span>
                            </div>
                          }
                          @if (getTotalRejectPartsAcrossBatches() > 0) {
                            <div class="stat">
                              <span class="stat-value reject">{{ getTotalRejectPartsAcrossBatches() }}</span>
                              <span class="stat-label">Reject</span>
                            </div>
                          }
                        </div>
                      </div>
                    }
                    <!-- Batches list (scrollable, max 3 visible) -->
                    @if (!loadingBatches && event.batches && event.batches.length > 0) {
                      <div class="batches-list-container"
                        >
                        <div class="batches-list">
                          @for (batch of event.batches; track batch; let i = $index) {
                            <div class="batch-item"
                              [class.historical]="batch.is_historical">
                              <div class="batch-row">
                                <div class="batch-main">
                                  <div class="batch-title">
                                    <span class="batch-number">Job {{ i + 1 }}</span>
                                    @if (batch.batch_number) {
                                      <span class="batch-id">{{ batch.batch_number
                                      }}</span>
                                    }
                                    @if (batch.is_historical) {
                                      <span class="badge bg-secondary ms-2"
                                        matTooltip="This job has moved to another equipment">
                                        Historical
                                      </span>
                                    }
                                  </div>
                                  <div class="batch-info">
                                    @if (batch.work_order) {
                                      <span class="info-item">
                                        <i class="fa fa-clipboard-list"></i> {{ batch.work_order }}
                                      </span>
                                    }
                                  </div>
                                  <div class="batch-timing">
                                    <span class="timing-item">
                                      <i class="fa fa-play"></i> {{ formatTimestamp(batch.start_ts) }}
                                    </span>
                                    @if (batch.end_ts) {
                                      <span class="timing-item">
                                        <i class="fa fa-stop"></i> {{ formatTimestamp(batch.end_ts) }}
                                      </span>
                                    }
                                  </div>
                                  @if (batch.good_parts !== undefined || batch.reject_parts !== undefined) {
                                    <div class="batch-parts-row"
                                      >
                                      @if (batch.good_parts !== undefined) {
                                        <span class="parts-chip good">
                                          <i class="fa fa-check"></i> {{ batch.good_parts }} good
                                        </span>
                                      }
                                      @if (batch.reject_parts !== undefined && batch.reject_parts > 0) {
                                        <span class="parts-chip reject">
                                          <i class="fa fa-times"></i> {{ batch.reject_parts }} reject
                                        </span>
                                      }
                                      @if (batch.result === 'aborted' && batch.abort_reason) {
                                        <span class="batch-abort-reason">
                                          <span class="abort-code">{{ batch.abort_reason.code }}</span>
                                          {{ batch.abort_reason.name }}
                                        </span>
                                      }
                                    </div>
                                  }
                                  <!-- Custom Field Values (at-a-glance display) -->
                                  @if (batch.custom_field_values && batch.custom_field_values.length > 0) {
                                    <div class="batch-custom-fields"
                                      >
                                      <div class="custom-fields-row">
                                        @for (cfv of batch.custom_field_values; track cfv) {
                                          <span class="custom-field-chip"
                                            [matTooltip]="cfv.field_label">
                                            <span class="cf-label">{{ cfv.field_label }}:</span>
                                            <span class="cf-value">{{ formatCustomFieldValue(cfv) }}</span>
                                          </span>
                                        }
                                      </div>
                                    </div>
                                  }
                                </div>
                                <div class="batch-side">
                                  <span class="batch-status" [ngClass]="getBatchStatusClass(batch)">
                                    {{ getBatchStatusDisplay(batch) }}
                                  </span>
                                  <div class="batch-actions d-flex gap-1">
                                    @if (!readOnly && batch.result === 'running') {
                                      <button mat-stroked-button color="primary" class="complete-btn"
                                        (click)="openCompleteBatchModalForBatch(batch); $event.stopPropagation()">
                                        <mat-icon>check_circle</mat-icon>
                                        Complete
                                      </button>
                                      <button mat-stroked-button color="warn" class="abort-btn"
                                        (click)="openAbortBatchModalForBatch(batch); $event.stopPropagation()">
                                        <mat-icon>cancel</mat-icon>
                                        Abort
                                      </button>
                                    }
                                    @if (!readOnly && (batch.result === 'aborted' || batch.status === 'aborted')) {
                                      <button mat-stroked-button color="primary" class="reopen-btn"
                                        (click)="reopenBatchForBatch(batch); $event.stopPropagation()">
                                        <mat-icon>refresh</mat-icon>
                                        Reopen
                                      </button>
                                    }
                                  </div>
                                </div>
                              </div>
                            </div>
                          }
                        </div>
                      </div>
                    }
                  </div>
                }
              </div>
            }
            <!-- Single Batch Details Card (for downtime events with associated batch) -->
            @if (event.type !== 'batch' && event.batch) {
              <div class="info-card">
                <div class="card-header">
                  <i class="fa fa-box"></i>
                  <h3>Job Details</h3>
                  <span class="batch-status-badge" [ngClass]="getBatchStatusClass(event.batch)">
                    {{ getBatchStatusDisplay(event.batch) }}
                  </span>
                </div>
                <div class="card-content">
                  @if (event.batch.work_order) {
                    <div class="info-row">
                      <span class="label">Work Order</span>
                      <span class="value">{{ event.batch.work_order }}</span>
                    </div>
                  }
                  @if (event.batch.product_name) {
                    <div class="info-row">
                      <span class="label">Product</span>
                      <span class="value">
                        @if (event.batch.product_id) {
                          <a [routerLink]="['/oee/products', event.batch.product_id]" target="_blank">{{ event.batch.product_name }}@if (event.batch.product_code) { ({{ event.batch.product_code }})}</a>
                        } @else {
                          {{ event.batch.product_name }}@if (event.batch.product_code) { ({{ event.batch.product_code }})}
                        }
                      </span>
                    </div>
                  }
                  @if (event.batch.part_count) {
                    <div class="info-row">
                      <span class="label">Total Parts</span>
                      <span class="value">{{ event.batch.part_count }}</span>
                    </div>
                  }
                  @if (event.batch.good_parts !== undefined) {
                    <div class="info-row">
                      <span class="label">Good Parts</span>
                      <span class="value good">{{ event.batch.good_parts }}</span>
                    </div>
                  }
                  @if (event.batch.reject_parts !== undefined) {
                    <div class="info-row">
                      <span class="label">Reject Parts</span>
                      <span class="value reject">{{ event.batch.reject_parts }}</span>
                    </div>
                  }
                  @if (event.batch.result) {
                    <div class="info-row">
                      <span class="label">Result</span>
                      <span class="value result-badge" [class.success]="event.batch.result === 'success'"
                        [class.failure]="event.batch.result === 'failure'">
                        {{ batchResultDisplayLabel(event.batch.result) }}
                      </span>
                    </div>
                  }
                  <!-- Custom Field Values -->
                  @for (fieldValue of event.batch.custom_field_values; track fieldValue) {
                    <div class="info-row">
                      <span class="label">{{ getCustomFieldDisplayLabel(fieldValue) }}</span>
                      <span class="value">{{ fieldValue.value || '—' }}</span>
                    </div>
                  }
                </div>
                <!-- Complete Job Button -->
                @if (!readOnly && event.batch.result !== 'completed' && event.batch.result !== 'approved' && event.batch.result !== 'aborted') {
                  <div class="card-actions">
                    <button mat-stroked-button color="primary" (click)="openCompleteBatchModal()">
                      <mat-icon>check_circle</mat-icon>
                      {{ completeBatchActionLabel }}
                    </button>
                  </div>
                }
              </div>
            }
            <!-- Notes Card -->
            @if (event.notes || event.troubleshooting_notes || event.resolution_notes) {
              <div class="info-card">
                <div class="card-header">
                  <i class="fa fa-sticky-note"></i>
                  <h3>Notes History</h3>
                </div>
                <div class="card-content notes-content">
                  @if (event.notes) {
                    <div class="note-section">
                      <span class="note-label">Initial Notes</span>
                      <p class="note-text">{{ event.notes }}</p>
                    </div>
                  }
                  @if (event.troubleshooting_notes) {
                    <div class="note-section">
                      <span class="note-label">
                        <i class="fa fa-wrench"></i>
                        Troubleshooting Notes
                      </span>
                      <p class="note-text">{{ event.troubleshooting_notes }}</p>
                    </div>
                  }
                  @if (event.resolution_notes) {
                    <div class="note-section">
                      <span class="note-label">
                        <i class="fa fa-check-circle"></i>
                        Resolution Notes
                      </span>
                      <p class="note-text">{{ event.resolution_notes }}</p>
                    </div>
                  }
                </div>
              </div>
            }
            <!-- Workers Notes Card (for batch events with time entry notes) -->
            @if (isBatchTypeEvent() && workerNotes.length > 0) {
              <div class="info-card worker-notes-card">
                <div class="card-header">
                  <i class="fa fa-hard-hat"></i>
                  <h3>Workers Notes</h3>
                  <span class="linked-count-badge">
                    {{ workerNotes.length }} note{{ workerNotes.length !== 1 ? 's' : '' }}
                  </span>
                </div>
                <div class="card-content worker-notes-content">
                  <app-worker-notes-list [notes]="workerNotes"></app-worker-notes-list>
                </div>
              </div>
            }
            <!-- Batch Attachments Card (M1: mock-backed; real wiring lands in M6) -->
            @if (resolvedBatchNumber()) {
              <div class="info-card attachments-card">
                <div class="card-header">
                  <i class="fa fa-paperclip"></i>
                  <h3>Attachments</h3>
                </div>
                <div class="card-content p-0 attachments-content">
                  <app-batch-attachments-list
                    [batchNumber]="resolvedBatchNumber()"
                    [readonly]="!isEditMode">
                  </app-batch-attachments-list>
                </div>
              </div>
            }
            <!-- Audit Info Card -->
            <div class="info-card audit-card">
              <div class="card-header">
                <i class="fa fa-history"></i>
                <h3>Audit Information</h3>
                @if (hasEditHistory()) {
                  <button mat-icon-button (click)="toggleEditHistory()"
                    matTooltip="View Edit History">
                    <mat-icon>{{ showEditHistory ? 'expand_less' : 'expand_more' }}</mat-icon>
                  </button>
                }
              </div>
              <div class="card-content">
                <div class="info-row">
                  <span class="label">Created</span>
                  <span class="value">{{ formatTimestamp(event.created_at) }}</span>
                </div>
                @if (event.created_by_name || event.created_by) {
                  <div class="info-row">
                    <span class="label">Created By</span>
                    <span class="value">{{ getUserDisplayName(event.created_by, event.created_by_name) }}</span>
                  </div>
                }
                @if (event.end_ts && isBatchTypeEvent()) {
                  <div class="info-row">
                    <span class="label">Completed</span>
                    <span class="value">{{ formatTimestamp(event.end_ts) }}</span>
                  </div>
                }
                @if (event.completed_by_name || event.completed_by) {
                  <div class="info-row">
                    <span class="label">Completed By</span>
                    <span class="value">{{ event.completed_by_name || event.completed_by }}</span>
                  </div>
                }
                @if (hasEditHistory()) {
                  <div class="info-row">
                    <span class="label">Last Edited</span>
                    <span class="value">{{ formatTimestamp(event.edit_history![0].edited_at) }}</span>
                  </div>
                }
              </div>
              <!-- Edit History (collapsible) -->
              @if (showEditHistory && hasEditHistory()) {
                <div class="edit-history">
                  @for (edit of event.edit_history; track edit) {
                    <div class="history-item">
                      <div class="history-header">
                        <span class="edit-date">{{ formatTimestamp(edit.edited_at) }}</span>
                        <span class="edit-by">by {{ edit.edited_by }}</span>
                      </div>
                      @if (edit.edit_comment) {
                        <div class="edit-comment">{{ edit.edit_comment }}</div>
                      }
                      <div class="changes-list">
                        @for (field of edit.fields_changed; track field) {
                          <div class="change">
                            <span class="field-name">{{ getFieldDisplayName(field.field_name) }}:</span>
                            <span class="old-value">{{ formatFieldValue(field.field_name, field.old_value) }}</span>
                            <i class="fa fa-arrow-right"></i>
                            <span class="new-value">{{ formatFieldValue(field.field_name, field.new_value) }}</span>
                          </div>
                        }
                      </div>
                    </div>
                  }
                </div>
              }
            </div>
          </div>
        }
        <!-- EDIT MODE -->
        @if (isEditMode) {
          <div class="edit-mode-content">
            <form [formGroup]="editForm">
              <!-- Timing Section -->
              <div class="edit-section">
                <h4 class="section-toggle" (click)="toggleEditSection('timing')">
                  <i class="fa fa-clock"></i> Timing
                  <mat-icon class="expand-icon">{{ isEditSectionExpanded('timing') ? 'expand_less' : 'expand_more' }}</mat-icon>
                </h4>
                @if (isEditSectionExpanded('timing')) {
                  <div class="form-row">
                    <app-datetime-picker formControlName="start_ts" label="Start Time" prefixIcon="schedule">
                    </app-datetime-picker>
                    <app-datetime-picker formControlName="end_ts" label="End Time" prefixIcon="schedule">
                    </app-datetime-picker>
                  </div>
                  @if (editForm.get('end_ts')?.hasError('invalidTimeRange')) {
                    <mat-error class="time-range-error">
                      End time must be after start time
                    </mat-error>
                  }
                  <!-- Clear end date link (only show for completed batch events) -->
                  @if (isBatchTypeEvent() && isCompletedBatch()) {
                    <div class="clear-end-date-link">
                      <a href="javascript:void(0)" (click)="onClearEndDate()" class="text-link">
                        <mat-icon>replay</mat-icon>
                        Clear end date to resume job
                      </a>
                    </div>
                  }
                }
              </div>
              <!-- Linked Equipment Section (for downtime and incident events, not station events) -->
              @if (!isStationEvent && (event.type === 'downtime' || event.type === 'incident')) {
                <div class="edit-section">
                  <h4 class="section-toggle" (click)="toggleEditSection('linkedEquipment')">
                    <i class="fa fa-link"></i> Linked Equipment
                    <mat-icon class="expand-icon">{{ isEditSectionExpanded('linkedEquipment') ? 'expand_less' : 'expand_more' }}</mat-icon>
                  </h4>
                  @if (isEditSectionExpanded('linkedEquipment')) {
                  <!-- Current linked equipment summary (when collapsed) -->
                  @if (!showMultiEquipmentSelect) {
                    <div class="mb-2">
                      <span class="text-muted">{{ getSelectedEquipmentNames().join(', ') || event.equipment_name }}</span>
                      @if (allEquipment.length > 0) {
                        <button type="button" class="btn btn-sm btn-link p-0 ms-2" (click)="toggleMultiEquipment()"
                          >
                          <i class="fa fa-edit"></i> Edit
                        </button>
                      }
                    </div>
                  }
                  <!-- Equipment selection (when expanded) -->
                  @if (showMultiEquipmentSelect && allEquipment.length > 0) {
                    <div class="equipment-selection-section">
                      <!-- Search and actions -->
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <div class="equipment-search flex-grow-1 me-2">
                          <input type="text" class="form-control form-control-sm"
                            placeholder="Search equipment..."
                            [(ngModel)]="equipmentSearchQuery"
                            [ngModelOptions]="{standalone: true}">
                          </div>
                          <div class="btn-group btn-group-sm">
                            <button type="button" class="btn btn-outline-secondary" (click)="selectAllEquipment()">
                              Select All
                            </button>
                            <button type="button" class="btn btn-outline-secondary" (click)="toggleMultiEquipment()">
                              Done
                            </button>
                          </div>
                        </div>
                        <!-- Equipment list - ALL equipment shown -->
                        <div class="equipment-list" style="max-height: 200px; overflow-y: auto; border: 1px solid #dee2e6; border-radius: 4px; padding: 8px;">
                          @for (eq of getFilteredEquipment(); track eq) {
                            <label class="d-flex align-items-center gap-2 py-1" style="cursor: pointer;"
                              >
                              <input type="checkbox"
                                [checked]="isEquipmentSelected(eq.id)"
                                (click)="toggleEquipmentSelection(eq.id, $event)">
                                <span>{{ eq.name }}</span>
                                @if (eq.code) {
                                  <span class="text-muted small">#{{ eq.code }}</span>
                                }
                              </label>
                            }
                            @if (getFilteredEquipment().length === 0) {
                              <div class="text-muted text-center py-2">
                                <i class="fa fa-search me-1"></i> No equipment matches your search
                              </div>
                            }
                          </div>
                          <!-- Selected count and validation -->
                          <div class="mt-1">
                            <span [class.text-muted]="selectedEquipmentIds.length > 0"
                              [class.text-danger]="selectedEquipmentIds.length === 0"
                              class="small">
                              @if (selectedEquipmentIds.length === 0) {
                                <i class="fa fa-exclamation-triangle me-1"></i>
                              }
                              {{ selectedEquipmentIds.length }} equipment selected
                              @if (hasEquipmentLinksChanged()) {
                                <span class="ms-1">
                                  <i class="fa fa-pencil-alt"></i> Modified
                                </span>
                              }
                            </span>
                          </div>
                        </div>
                      }
                  }
                    </div>
                  }
                  <!-- Associated Batches Section (for downtime/incident events) -->
                  @if (event.type === 'downtime' || event.type === 'incident') {
                    <div class="edit-section">
                      <h4 class="section-toggle" (click)="toggleEditSection('associatedBatches')">
                        <i class="fa fa-boxes"></i> Associated Batches
                        <mat-icon class="expand-icon">{{ isEditSectionExpanded('associatedBatches') ? 'expand_less' : 'expand_more' }}</mat-icon>
                      </h4>
                      @if (isEditSectionExpanded('associatedBatches')) {
                      <!-- Current linked jobs summary (when collapsed) -->
                      @if (!showBatchLinkSelect) {
                        <div class="mb-2">
                          @if (selectedBatchEventIds.length === 0) {
                            <span class="text-muted">No jobs associated</span>
                          } @else {
                            <span class="text-muted">{{ selectedBatchEventIds.length }} batch{{ selectedBatchEventIds.length !== 1 ? 'es' : '' }} associated</span>
                          }
                          <button type="button" class="btn btn-sm btn-link p-0 ms-2" (click)="toggleBatchLinkSelect()">
                            <i class="fa fa-edit"></i> Edit
                          </button>
                        </div>
                      }
                      <!-- Batch selection (when expanded) -->
                      @if (showBatchLinkSelect) {
                        <div class="batch-link-selection-section">
                          <!-- Search and actions -->
                          <div class="d-flex justify-content-between align-items-center mb-2">
                            <div class="flex-grow-1 me-2">
                              <input type="text" class="form-control form-control-sm"
                                placeholder="Search by batch number, work order, or equipment..."
                                [(ngModel)]="batchLinkSearchQuery"
                                (ngModelChange)="batchLinkPage = 1"
                                [ngModelOptions]="{standalone: true}">
                            </div>
                            <div class="btn-group btn-group-sm">
                              <button type="button" class="btn btn-outline-secondary" (click)="selectAllBatchesForLinking()">
                                Select All
                              </button>
                              <button type="button" class="btn btn-outline-secondary" (click)="clearBatchSelections()">
                                Clear
                              </button>
                              <button type="button" class="btn btn-outline-secondary" (click)="toggleBatchLinkSelect()">
                                Done
                              </button>
                            </div>
                          </div>
                          @if (loadingAvailableBatches) {
                            <div class="text-center py-3">
                              <mat-spinner diameter="24"></mat-spinner>
                              <p class="text-muted small mt-1">Loading batches...</p>
                            </div>
                          }
                          @if (!loadingAvailableBatches) {
                            <!-- Batch list -->
                            <div class="batch-link-list" style="max-height: 250px; overflow-y: auto; border: 1px solid #dee2e6; border-radius: 4px; padding: 8px;">
                              @for (batch of getPaginatedBatchesForLinking(); track batch.batch_id) {
                                <label class="d-flex align-items-start gap-2 py-1 border-bottom" style="cursor: pointer;">
                                  <input type="checkbox" class="mt-1"
                                    [checked]="isBatchEventSelected(batch.batch_id)"
                                    (change)="toggleBatchEventSelection(batch.batch_id)">
                                  <div class="flex-grow-1">
                                    <div class="d-flex align-items-center gap-2">
                                      <span class="fw-semibold">{{ batch.batch_number }}</span>
                                      @if (batch.work_order) {
                                        <span class="text-muted small">WO: {{ batch.work_order }}</span>
                                      }
                                    </div>
                                    <div class="small text-muted">
                                      <span>{{ batch.equipment_name }}</span>
                                      <span class="ms-2">{{ formatTimestamp(batch.start_ts) }}</span>
                                    </div>
                                  </div>
                                </label>
                              }
                              @if (getFilteredBatchesForLinking().length === 0) {
                                <div class="text-muted text-center py-2">
                                  @if (availableBatchesForLinking.length === 0) {
                                    No jobs found in the last 90 days
                                  } @else {
                                    <i class="fa fa-search me-1"></i> No jobs match your search
                                  }
                                </div>
                              }
                            </div>
                            <!-- Pagination controls -->
                            @if (getBatchLinkTotalPages() > 1) {
                              <div class="d-flex justify-content-between align-items-center mt-2">
                                <span class="text-muted small">
                                  Page {{ batchLinkPage }} of {{ getBatchLinkTotalPages() }}
                                </span>
                                <div class="btn-group btn-group-sm">
                                  <button type="button" class="btn btn-outline-secondary"
                                    [disabled]="batchLinkPage === 1"
                                    (click)="setBatchLinkPage(1)">
                                    <i class="fa fa-angle-double-left"></i>
                                  </button>
                                  <button type="button" class="btn btn-outline-secondary"
                                    [disabled]="batchLinkPage === 1"
                                    (click)="setBatchLinkPage(batchLinkPage - 1)">
                                    <i class="fa fa-angle-left"></i>
                                  </button>
                                  <button type="button" class="btn btn-outline-secondary"
                                    [disabled]="batchLinkPage === getBatchLinkTotalPages()"
                                    (click)="setBatchLinkPage(batchLinkPage + 1)">
                                    <i class="fa fa-angle-right"></i>
                                  </button>
                                  <button type="button" class="btn btn-outline-secondary"
                                    [disabled]="batchLinkPage === getBatchLinkTotalPages()"
                                    (click)="setBatchLinkPage(getBatchLinkTotalPages())">
                                    <i class="fa fa-angle-double-right"></i>
                                  </button>
                                </div>
                              </div>
                            }
                            <!-- Selected count -->
                            <div class="mt-1">
                              <span class="text-muted small">
                                {{ selectedBatchEventIds.length }} batch{{ selectedBatchEventIds.length !== 1 ? 'es' : '' }} selected
                                @if (hasBatchLinksChanged()) {
                                  <span class="ms-1">
                                    <i class="fa fa-pencil-alt"></i> Modified
                                  </span>
                                }
                              </span>
                            </div>
                          }
                        </div>
                      }
                      }
                    </div>
                  }
                  <!-- Batch Details Section (only for job-type events) -->
                  @if (isBatchTypeEvent()) {
                    <div class="edit-section">
                      <h4 class="section-toggle" (click)="toggleEditSection('batchDetails')">
                        <i class="fa fa-box"></i> Job Details
                        <mat-icon class="expand-icon">{{ isEditSectionExpanded('batchDetails') ? 'expand_less' : 'expand_more' }}</mat-icon>
                      </h4>
                      @if (isEditSectionExpanded('batchDetails')) {
                      <div class="form-row">
                        <mat-form-field appearance="outline">
                          <mat-label>Job Number</mat-label>
                          <input matInput formControlName="batch_number" [matAutocomplete]="batchNameAuto">
                          <mat-autocomplete #batchNameAuto="matAutocomplete">
                            @for (item of filteredBatchNames | async; track item) {
                              <mat-option [value]="item.batch_number">
                                <div class="batch-autocomplete-option">
                                  <span class="batch-name">{{ item.batch_number }}</span>
                                  @if (item.last_equipment_name) {
                                    <span class="batch-hint">
                                      <span class="hint-label">Last:</span>
                                      {{ item.last_equipment_name }}
                                      @if (item.last_used_ts) {
                                        <span class="last-used">• {{ formatLastUsed(item.last_used_ts) }}</span>
                                      }
                                    </span>
                                  }
                                </div>
                              </mat-option>
                            }
                          </mat-autocomplete>
                          <mat-icon matPrefix>qr_code_2</mat-icon>
                        </mat-form-field>
                        <mat-form-field appearance="outline">
                          <mat-label>Work Order</mat-label>
                          <input matInput formControlName="work_order">
                          <mat-icon matPrefix>assignment</mat-icon>
                        </mat-form-field>
                      </div>
                      <div class="form-row">
                        <mat-form-field appearance="outline">
                          <mat-label>Started By</mat-label>
                          <mat-select formControlName="created_by">
                            @for (user of availableUsers; track user) {
                              <mat-option [value]="user.id">
                                {{ user.name || user.email }}
                              </mat-option>
                            }
                          </mat-select>
                          <mat-icon matPrefix>person</mat-icon>
                        </mat-form-field>
                        <mat-form-field appearance="outline">
                          <mat-label>Completed By</mat-label>
                          <mat-select formControlName="completed_by">
                            <mat-option value="">-- Not Set --</mat-option>
                            @for (user of availableUsers; track user) {
                              <mat-option [value]="user.id">
                                {{ user.name || user.email }}
                              </mat-option>
                            }
                          </mat-select>
                          <mat-icon matPrefix>person_outline</mat-icon>
                        </mat-form-field>
                      </div>
                      <!-- Product -->
                      <div class="form-row">
                        <mat-form-field appearance="outline">
                          <mat-label>Product</mat-label>
                          <mat-select formControlName="product_id">
                            <!-- Product is required for batches -->
                            @for (product of availableProducts; track product) {
                              <mat-option [value]="product.id">
                                {{ product.product_name }}
                                @if (product.product_code) {
                                  <span class="product-code-hint"> ({{ product.product_code }})</span>
                                }
                              </mat-option>
                            }
                          </mat-select>
                          <mat-icon matPrefix>inventory_2</mat-icon>
                          @if (loadingProducts) {
                            <mat-hint>Loading products...</mat-hint>
                          }
                        </mat-form-field>
                      </div>
                      <div class="form-row">
                        <mat-form-field appearance="outline">
                          <mat-label>Good Parts</mat-label>
                          <input matInput type="number" formControlName="good_parts" min="0">
                          <mat-icon matPrefix class="success-icon">check_circle</mat-icon>
                        </mat-form-field>
                        <mat-form-field appearance="outline">
                          <mat-label>Scrap Parts</mat-label>
                          <input matInput type="number" formControlName="reject_parts" min="0">
                          <mat-icon matPrefix class="error-icon">cancel</mat-icon>
                        </mat-form-field>
                      </div>
                      }
                    </div>
                  }
                  <!-- Custom Fields Section (only for job-type events with custom fields) -->
                  @if (isBatchTypeEvent() && customFields.length > 0) {
                    <div class="edit-section">
                      <h4 class="section-toggle" (click)="toggleEditSection('customFields')">
                        <i class="fa fa-sliders-h"></i> Custom Fields
                        <mat-icon class="expand-icon">{{ isEditSectionExpanded('customFields') ? 'expand_less' : 'expand_more' }}</mat-icon>
                      </h4>
                      @if (isEditSectionExpanded('customFields')) {
                      @if (loadingCustomFields) {
                        <mat-spinner diameter="20"></mat-spinner>
                      }
                      @if (!loadingCustomFields) {
                        <div class="custom-fields-grid">
                          @for (cf of customFields; track cf) {
                            <!-- Text field -->
                            @if (cf.type === 'text') {
                              <mat-form-field appearance="outline">
                                <mat-label>{{ cf.label }}</mat-label>
                                <input matInput [formControlName]="'custom_field_' + cf.id">
                                @if (cf.is_required) {
                                  <mat-hint>Required</mat-hint>
                                }
                              </mat-form-field>
                            }
                            <!-- Number field -->
                            @if (cf.type === 'number') {
                              <mat-form-field appearance="outline">
                                <mat-label>{{ cf.label }}</mat-label>
                                <input matInput type="number" [formControlName]="'custom_field_' + cf.id">
                                @if (cf.is_required) {
                                  <mat-hint>Required</mat-hint>
                                }
                              </mat-form-field>
                            }
                            <!-- Dropdown field -->
                            @if (cf.type === 'dropdown') {
                              <mat-form-field appearance="outline">
                                <mat-label>{{ cf.label }}</mat-label>
                                <mat-select [formControlName]="'custom_field_' + cf.id">
                                  <mat-option value="">-- Select --</mat-option>
                                  @for (opt of cf.dropdown_options; track opt) {
                                    <mat-option [value]="opt">{{ opt }}</mat-option>
                                  }
                                </mat-select>
                                @if (cf.is_required) {
                                  <mat-hint>Required</mat-hint>
                                }
                              </mat-form-field>
                            }
                            <!-- Date field -->
                            @if (cf.type === 'date') {
                              <mat-form-field appearance="outline">
                                <mat-label>{{ cf.label }}</mat-label>
                                <input matInput type="date" [formControlName]="'custom_field_' + cf.id">
                                @if (cf.is_required) {
                                  <mat-hint>Required</mat-hint>
                                }
                              </mat-form-field>
                            }
                            <!-- Checkbox field -->
                            @if (cf.type === 'checkbox') {
                              <div class="checkbox-field">
                                <mat-checkbox [formControlName]="'custom_field_' + cf.id">
                                  {{ cf.label }}
                                </mat-checkbox>
                                @if (cf.is_required) {
                                  <span class="required-hint">(Required)</span>
                                }
                              </div>
                            }
                          }
                        </div>
                      }
                      }
                    </div>
                  }
                  @if (isBatchTypeEvent() && event.batch && (equipmentDetails?.allows_batch_operators !== false || equipmentDetails?.restrict_batch_equipment === true)) {
                    <div class="edit-section">
                      <h4 class="section-toggle" (click)="toggleEditSection('timeEntries')">
                        <i class="fa fa-user-clock"></i> Time Entries
                        <mat-icon class="expand-icon">{{ isEditSectionExpanded('timeEntries') ? 'expand_less' : 'expand_more' }}</mat-icon>
                      </h4>
                      @if (isEditSectionExpanded('timeEntries')) {
                        <app-batch-time-entries
                            [batchId]="event.batch.batch_id"
                            [equipmentId]="event.equipment_id"
                            [siteId]="selectedSiteId"
                            mode="full"
                            [readOnly]="false">
                        </app-batch-time-entries>
                      }
                    </div>
                  }
                  <!-- Associated Downtime/Incidents Section (for job-type events) -->
                  @if (isBatchTypeEvent()) {
                    <div class="edit-section">
                      <h4 class="section-toggle" (click)="toggleEditSection('associatedEvents')">
                        <i class="fa fa-exclamation-triangle"></i> Associated Events
                        <mat-icon class="expand-icon">{{ isEditSectionExpanded('associatedEvents') ? 'expand_less' : 'expand_more' }}</mat-icon>
                      </h4>
                      @if (isEditSectionExpanded('associatedEvents')) {
                      <!-- Current linked events summary (when collapsed) -->
                      @if (!showEventLinkSelect) {
                        <div class="mb-2">
                          @if (selectedLinkedEventIds.length === 0) {
                            <span class="text-muted">No downtime/incidents associated</span>
                          } @else {
                            <span class="text-muted">{{ selectedLinkedEventIds.length }} event{{ selectedLinkedEventIds.length !== 1 ? 's' : '' }} associated</span>
                          }
                          <button type="button" class="btn btn-sm btn-link p-0 ms-2" (click)="toggleEventLinkSelect()">
                            <i class="fa fa-edit"></i> Edit
                          </button>
                        </div>
                      }
                      <!-- Event selection (when expanded) -->
                      @if (showEventLinkSelect) {
                        <div class="event-link-selection-section">
                          <!-- Search and actions -->
                          <div class="d-flex justify-content-between align-items-center mb-2">
                            <div class="flex-grow-1 me-2">
                              <input type="text" class="form-control form-control-sm"
                                placeholder="Search by reason, equipment, or notes..."
                                [(ngModel)]="eventLinkSearchQuery"
                                [ngModelOptions]="{standalone: true}">
                            </div>
                            <div class="btn-group btn-group-sm">
                              <button type="button" class="btn btn-outline-secondary" (click)="selectAllEventsForLinking()">
                                Select All
                              </button>
                              <button type="button" class="btn btn-outline-secondary" (click)="clearEventSelections()">
                                Clear
                              </button>
                              <button type="button" class="btn btn-outline-secondary" (click)="toggleEventLinkSelect()">
                                Done
                              </button>
                            </div>
                          </div>
                          @if (loadingAvailableEvents) {
                            <div class="text-center py-3">
                              <mat-spinner diameter="24"></mat-spinner>
                              <p class="text-muted small mt-1">Loading events...</p>
                            </div>
                          }
                          @if (!loadingAvailableEvents) {
                            <!-- Event list -->
                            <div class="event-link-list" style="max-height: 250px; overflow-y: auto; border: 1px solid #dee2e6; border-radius: 4px; padding: 8px;">
                              @for (evt of getFilteredEventsForLinking(); track evt.event_id) {
                                <label class="d-flex align-items-start gap-2 py-1 border-bottom" style="cursor: pointer;">
                                  <input type="checkbox" class="mt-1"
                                    [checked]="isLinkedEventSelected(evt.event_id)"
                                    (change)="toggleLinkedEventSelection(evt.event_id)">
                                  <div class="flex-grow-1">
                                    <div class="d-flex align-items-center gap-2">
                                      @if (evt.type === 'downtime') {
                                        <i class="fa fa-pause-circle text-danger small"></i>
                                      } @else {
                                        <i class="fa fa-exclamation-circle text-warning small"></i>
                                      }
                                      <span class="fw-semibold">{{ getEventDescription(evt) }}</span>
                                      <span class="badge badge-sm" [class.bg-danger]="evt.type === 'downtime'"
                                        [class.bg-warning]="evt.type === 'incident'">
                                        {{ evt.type }}
                                      </span>
                                    </div>
                                    <div class="small text-muted">
                                      <span>{{ formatTimestamp(evt.start_ts) }}</span>
                                      @if (evt.duration_minutes) {
                                        <span class="ms-2">{{ formatDuration(evt.duration_minutes) }}</span>
                                      }
                                    </div>
                                  </div>
                                </label>
                              }
                              @if (getFilteredEventsForLinking().length === 0) {
                                <div class="text-muted text-center py-2">
                                  @if (availableEventsForLinking.length === 0) {
                                    No downtime/incident events found for this equipment
                                  } @else {
                                    <i class="fa fa-search me-1"></i> No events match your search
                                  }
                                </div>
                              }
                            </div>
                            <!-- Selected count -->
                            <div class="mt-1">
                              <span class="text-muted small">
                                {{ selectedLinkedEventIds.length }} event{{ selectedLinkedEventIds.length !== 1 ? 's' : '' }} selected
                                @if (hasEventLinksChanged()) {
                                  <span class="ms-1">
                                    <i class="fa fa-pencil-alt"></i> Modified
                                  </span>
                                }
                              </span>
                            </div>
                          }
                        </div>
                      }
                      }
                    </div>
                  }
                  <!-- Downtime Details Section (only for downtime events) -->
                  @if (event.type === 'downtime') {
                    <div class="edit-section">
                      <h4 class="section-toggle" (click)="toggleEditSection('downtimeDetails')">
                        <i class="fa fa-exclamation-triangle"></i> Downtime Details
                        <mat-icon class="expand-icon">{{ isEditSectionExpanded('downtimeDetails') ? 'expand_less' : 'expand_more' }}</mat-icon>
                      </h4>
                      @if (isEditSectionExpanded('downtimeDetails')) {
                      <div class="form-row">
                        <mat-form-field appearance="outline" class="full-width">
                          <mat-label>Downtime Reason</mat-label>
                          <mat-select formControlName="downtime_reason_id">
                            @for (reason of availableDowntimeReasons; track reason) {
                              <mat-option [value]="reason.reason_id">
                                {{ reason.code }} - {{ reason.display_name }}
                              </mat-option>
                            }
                          </mat-select>
                        </mat-form-field>
                        <mat-form-field appearance="outline">
                          <mat-label>Created By</mat-label>
                          <mat-select formControlName="created_by">
                            @for (user of availableUsers; track user) {
                              <mat-option [value]="user.id">
                                {{ user.name || user.email }}
                              </mat-option>
                            }
                          </mat-select>
                        </mat-form-field>
                      </div>
                      }
                    </div>
                  }
                  <!-- Incident Details Section (only for incident events) -->
                  @if (event.type === 'incident') {
                    <div class="edit-section">
                      <h4 class="section-toggle" (click)="toggleEditSection('incidentDetails')">
                        <i class="fa fa-exclamation-circle"></i> Incident Details
                        <mat-icon class="expand-icon">{{ isEditSectionExpanded('incidentDetails') ? 'expand_less' : 'expand_more' }}</mat-icon>
                      </h4>
                      @if (isEditSectionExpanded('incidentDetails')) {
                      <div class="form-row">
                        <mat-form-field appearance="outline" class="full-width">
                          <mat-label>Incident Reason</mat-label>
                          <mat-select formControlName="incident_reason_id">
                            @for (group of incidentReasonGroups; track group) {
                              <mat-optgroup [label]="group.name">
                                @for (reason of group.reasons; track reason) {
                                  <mat-option [value]="reason.id">
                                    {{ reason.code }} - {{ reason.name }}
                                  </mat-option>
                                }
                              </mat-optgroup>
                            }
                          </mat-select>
                        </mat-form-field>
                      </div>
                      }
                    </div>
                  }
                  <!-- Troubleshooting Status Section (only for downtime events) -->
                  @if (event.type === 'downtime') {
                    <div class="edit-section">
                      <h4 class="section-toggle" (click)="toggleEditSection('troubleshooting')">
                        <i class="fa fa-wrench"></i> Troubleshooting Status
                        <mat-icon class="expand-icon">{{ isEditSectionExpanded('troubleshooting') ? 'expand_less' : 'expand_more' }}</mat-icon>
                      </h4>
                      @if (isEditSectionExpanded('troubleshooting')) {
                      <div class="form-row">
                        <app-datetime-picker formControlName="troubleshooting_started_at"
                          label="Troubleshooting Started At" prefixIcon="build">
                        </app-datetime-picker>
                        <mat-form-field appearance="outline">
                          <mat-label>Troubleshooting Started By</mat-label>
                          <mat-select formControlName="troubleshooting_started_by">
                            @for (user of availableUsers; track user) {
                              <mat-option [value]="user.id">
                                {{ user.name || user.email }}
                              </mat-option>
                            }
                          </mat-select>
                        </mat-form-field>
                      </div>
                      <div class="form-row">
                        <app-datetime-picker formControlName="resolved_at" label="Resolved At"
                          prefixIcon="check_circle">
                        </app-datetime-picker>
                        <mat-form-field appearance="outline">
                          <mat-label>Resolved By</mat-label>
                          <mat-select formControlName="resolved_by">
                            @for (user of availableUsers; track user) {
                              <mat-option [value]="user.id">
                                {{ user.name || user.email }}
                              </mat-option>
                            }
                          </mat-select>
                        </mat-form-field>
                      </div>
                      }
                    </div>
                  }
                  <!-- Notes Section -->
                  <div class="edit-section">
                    <h4 class="section-toggle" (click)="toggleEditSection('notes')">
                      <i class="fa fa-sticky-note"></i> Notes
                      <mat-icon class="expand-icon">{{ isEditSectionExpanded('notes') ? 'expand_less' : 'expand_more' }}</mat-icon>
                    </h4>
                    @if (isEditSectionExpanded('notes')) {
                    <mat-form-field appearance="outline" class="full-width">
                      <mat-label>General Notes</mat-label>
                      <textarea matInput formControlName="notes" rows="3"></textarea>
                    </mat-form-field>
                    @if (event.type === 'downtime') {
                      <div>
                        <mat-form-field appearance="outline" class="full-width">
                          <mat-label>Troubleshooting Notes</mat-label>
                          <textarea matInput formControlName="troubleshooting_notes" rows="3"
                          placeholder="What steps were taken to diagnose the issue?"></textarea>
                        </mat-form-field>
                        <mat-form-field appearance="outline" class="full-width">
                          <mat-label>Resolution Notes</mat-label>
                          <textarea matInput formControlName="resolution_notes" rows="3"
                          placeholder="How was the issue resolved?"></textarea>
                        </mat-form-field>
                      </div>
                    }
                    }
                  </div>
                  <!-- Attachments (edit mode: allow add + delete) -->
                  @if (resolvedBatchNumber()) {
                    <div class="edit-section">
                      <h4 class="section-toggle" (click)="toggleEditSection('attachments')">
                        <i class="fa fa-paperclip"></i> Attachments
                        <mat-icon class="expand-icon">{{ isEditSectionExpanded('attachments') ? 'expand_less' : 'expand_more' }}</mat-icon>
                      </h4>
                      @if (isEditSectionExpanded('attachments')) {
                        <app-batch-attachments-list
                          [batchNumber]="resolvedBatchNumber()"
                          [readonly]="false">
                        </app-batch-attachments-list>
                      }
                    </div>
                  }
                  <!-- Edit Comment -->
                  <div class="edit-section">
                    <h4 class="section-toggle" (click)="toggleEditSection('editComment')">
                      <i class="fa fa-comment"></i> Edit Comment
                      <mat-icon class="expand-icon">{{ isEditSectionExpanded('editComment') ? 'expand_less' : 'expand_more' }}</mat-icon>
                    </h4>
                    @if (isEditSectionExpanded('editComment')) {
                    <mat-form-field appearance="outline" class="full-width">
                      <mat-label>Why are you making this change? (optional)</mat-label>
                      <textarea matInput formControlName="edit_comment" rows="2"></textarea>
                    </mat-form-field>
                    }
                  </div>
                </form>
              </div>
            }
          </div>
          }<!-- Close event view wrapper -->

        </mat-dialog-content>

        <mat-dialog-actions align="end">
          <app-detail-action-bar flavor="material" [actions]="footerActions"
                                 (action)="onFooterAction($event)">
          </app-detail-action-bar>
        </mat-dialog-actions>
      </div>
